import { useFormik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import WarningIconSvg from '../../../../assets/icons/warning-icon.svg';
import { MOBILE_BREAKPOINT, colors } from '../../../constants';
import { ActionButton, InlineButton } from '../../Button';
import Card from '../../Card';
import { DateTextInput } from '../../DateTextInput';
import { SsnTextInput } from '../../SsnTextInput';
import {
  validateDate,
  validateMaxAgeOrYounger,
  validateMinAgeOrOlder,
} from 'src/commons/utilities/dateValidation';
import { Logout } from 'src/commons/utilities/identity';
import { pxToRem } from 'src/commons/utilities/utility';

import IdentityError from './IdentityError';
import { IdentityHeading } from './IdentityHeading';

type IdentityProps = {
  readonly isRequireSSNField: boolean;
  readonly onContinue: (values: {
    dateOfBirth: string;
    last4SSN?: string;
  }) => void;
  readonly isIdentityError: boolean;
};

type RequireSSNFieldProps = {
  isRequireSSNField?: boolean;
};

const CardWrapper = styled(Card)`
  min-height: ${pxToRem(352)};
`;

CardWrapper.displayName = 'CardWrapper';
const IdentityContainer = styled.div`
  margin: ${pxToRem(24)} 0 0;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: ${pxToRem(8)} 0 0;
  }
`;
IdentityContainer.displayName = 'IdentityContainer';

// Form containing all text inputs and continue button
const Form = styled.form`
  width: ${pxToRem(400)};
  display: flex;
  flex-flow: column nowrap;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  padding-top: ${pxToRem(24)};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
  }
`;
Form.displayName = 'Form';

const FormControlWrapper = styled.div`
  margin-bottom: ${pxToRem(32)};
`;
FormControlWrapper.displayName = 'FormControlWrapper';

const FormControlInputWrapper = styled.div<RequireSSNFieldProps>`
  margin-bottom: ${(props) =>
    props.isRequireSSNField ? pxToRem(16) : pxToRem(40)};
`;
FormControlWrapper.displayName = 'FormControlWrapper';

const FormSection = styled.div`
  width: 100%;
  margin: 0.5rem 0;
`;
FormSection.displayName = 'FormSection';

const IconWrapper = styled.div`
  padding-top: ${pxToRem(48)};
`;

const LogoutUseNewEmail = styled(InlineButton)<RequireSSNFieldProps>`
  color: ${colors.TEXT_ANCHOR};
  font-weight: 700;
  font-size: ${pxToRem(18.7)};
  text-decoration: none;
  margin-bottom: ${pxToRem(48)};
  &:hover {
    text-decoration: underline;
  }
  &:focus:not(:hover, :focus-visible) {
    text-decoration: none;
    outline: none;
    margin: 1.5rem 1rem;
    padding: auto;
  }
`;

LogoutUseNewEmail.displayName = 'LogoutUseNewEmail';

const LogOutWrapper = styled.div`
  position: relative;
  text-align: center;
`;
LogOutWrapper.displayName = 'LogOutWrapper';
// Define a validation schema using Yup

const Identity = ({
  isRequireSSNField,
  isIdentityError,
  onContinue,
}: IdentityProps) => {
  const validationSchema = Yup.object({
    dateOfBirth: Yup.string()
      .required('Enter your date of birth')
      .test('is-valid-date', 'Enter a valid date', (value) =>
        validateDate(value)
      )
      .test('is-user-born-after-1900', 'Enter a valid date of birth', (value) =>
        validateMaxAgeOrYounger(value)
      )
      .test(
        'is-default-user-the-minimum-age-18-or-older',
        'Thanks for your interest, but you must be at least 18 years old to apply for a credit card through Prosper.',
        (value) => validateMinAgeOrOlder(18, value)
      ),
    ...(isRequireSSNField
      ? {
          last4SSN: Yup.string()
            .trim()
            .min(4, 'Enter the last 4 digits of your Social Security number')
            .max(4, 'Enter the last 4 digits of your Social Security number')
            .required('Enter the last 4 digits of your Social Security number'),
        }
      : {}),
  });

  const formik = useFormik({
    initialValues: {
      dateOfBirth: '',
      ...(isRequireSSNField ? { last4SSN: '' } : {}),
    },
    validationSchema,
    onSubmit: (values) => {
      onContinue(values);
    },
  });

  return (
    <CardWrapper>
      <IconWrapper>
        <WarningIconSvg />
      </IconWrapper>
      {!isIdentityError ? (
        <IdentityContainer>
          <IdentityHeading isRequireSSN={isRequireSSNField} />
          <Form onSubmit={formik.handleSubmit}>
            <FormSection>
              <FormControlInputWrapper isRequireSSNField={isRequireSSNField}>
                <DateTextInput
                  name="dateOfBirth"
                  value={formik.values.dateOfBirth}
                  label="Date of birth (MM/DD/YYYY)"
                  error={formik.errors.dateOfBirth}
                  touched={formik.touched.dateOfBirth}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </FormControlInputWrapper>
              {isRequireSSNField && (
                <FormControlInputWrapper>
                  <SsnTextInput
                    name="last4SSN"
                    value={formik.values.last4SSN}
                    label="Last four digits of Social Security number"
                    error={formik.errors.last4SSN}
                    touched={formik.touched.last4SSN}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isFullSsn={false}
                  />
                </FormControlInputWrapper>
              )}
              <FormControlWrapper>
                <ActionButton type="submit" data-testid="continue-cta">
                  Continue
                </ActionButton>
              </FormControlWrapper>
            </FormSection>
          </Form>
          <LogOutWrapper>
            <LogoutUseNewEmail
              onClick={() => {
                Logout();
              }}
            >
              Log out and use new email
            </LogoutUseNewEmail>
          </LogOutWrapper>
        </IdentityContainer>
      ) : (
        <IdentityError />
      )}
    </CardWrapper>
  );
};

export default Identity;
